/* import __COLOCATED_TEMPLATE__ from './add-mcp-server-modal.hbs'; */
/* RESPONSIBLE TEAM: team-data-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import { post } from 'embercom/lib/ajax';
import type IntlService from 'embercom/services/intl';
import type { Template } from './mcp-template-list';
import type Store from '@ember-data/store';
import type Token from 'embercom/models/custom-authentication/token';
import { taskFor } from 'ember-concurrency-ts';
import {
  MCP_SERVER_AUTHENTICATION_TYPES,
  SHOPIFY_URL_PLACEHOLDER,
} from 'embercom/models/workflow-connector/constants/mcp-templates';
import { action } from '@ember/object';

interface Args {
  onModalClose: () => void;
  selectedTemplate: Template;
}

export default class AddMcpServerModal extends Component<Args> {
  @service declare appService: $TSFixMe;
  @service declare router: $TSFixMe;
  @service declare redirectService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare store: Store;

  @tracked serverName = '';
  @tracked serverUrl = '';
  @tracked serverDescription = '';
  @tracked tokenValue = '';
  @tracked customAuthenticationTokens: Token[] | undefined;
  @tracked selectedTokenId: string | undefined;
  @tracked useToken = false;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.args.selectedTemplate.template) {
      this.serverName = this.args.selectedTemplate.name;
    }
    taskFor(this.loadCustomAuthenticationTokens).perform();
  }

  @task({ drop: true })
  *loadCustomAuthenticationTokens(): TaskGenerator<void> {
    this.customAuthenticationTokens = yield this.store.findAll('custom-authentication/token');
  }

  get tokenList() {
    return (
      this.customAuthenticationTokens?.map((token) => ({
        value: token.id,
        text: token.name,
      })) || []
    );
  }

  @action
  updateSelectedToken(tokenId: string) {
    this.selectedTokenId = tokenId;
    let selectedToken = this.customAuthenticationTokens?.find((token) => token.id === tokenId);
    if (selectedToken) {
      this.tokenValue = selectedToken.tokenValue;
    }
  }

  get isTemplateServer() {
    return this.args.selectedTemplate.template;
  }

  get modalTitle() {
    return this.isTemplateServer
      ? this.args.selectedTemplate.name
      : this.intl.t('workflow-connector.actions.mcp-actions.template-list.custom-mcp-title');
  }

  get submitButtonLabel() {
    return this.isTemplateServer
      ? this.intl.t(
          'workflow-connector.actions.mcp-actions.add-mcp-server-form.add-server-template-button',
          { name: this.args.selectedTemplate.name },
        )
      : this.intl.t(
          'workflow-connector.actions.mcp-actions.add-mcp-server-form.add-custom-server-button',
        );
  }

  get buildServerUrl() {
    if (this.isTemplateServer && this.args.selectedTemplate.name === 'Shopify') {
      return `${this.serverUrl}/api/mcp`;
    }

    return this.args.selectedTemplate.url;
  }

  get shopifyUrlPlaceholder() {
    return SHOPIFY_URL_PLACEHOLDER;
  }

  @task({ drop: true })
  *addMcpServer(): TaskGenerator<void> {
    try {
      if (this.useToken || this.selectedTokenId) {
        let serverData = {
          name: this.isTemplateServer ? this.args.selectedTemplate.name : this.serverName,
          url: this.isTemplateServer ? this.buildServerUrl : this.serverUrl,
          description: this.isTemplateServer
            ? this.args.selectedTemplate.description
            : this.serverDescription,
          token_id: this.selectedTokenId,
        };

        yield this.store.createRecord('workflow-connector/mcp-server', serverData).save();

        window.location.reload();
      } else if (this.isTemplateServer) {
        let response = yield post('/ember/workflow_connector/setup_mcp_actions/add_mcp_server', {
          app_id: this.appService.app.id,
          server_name: this.args.selectedTemplate.name,
          server_url: this.buildServerUrl,
          server_description: this.args.selectedTemplate.description,
          auth_type: this.args.selectedTemplate.authentication,
          token_id: this.selectedTokenId,
        });

        if (response.auth_type === 'oauth' && response.authorization_url) {
          this.redirectService.redirect(response.authorization_url);
        } else {
          window.location.reload();
        }
      } else {
        let response = yield post(
          '/ember/workflow_connector/setup_mcp_actions/add_custom_mcp_server',
          {
            app_id: this.appService.app.id,
            auth_type: MCP_SERVER_AUTHENTICATION_TYPES.oauth,
            server_name: this.serverName,
            server_url: this.serverUrl,
            server_description: this.serverDescription,
          },
        );

        if (response.requires_oauth && response.authorization_url) {
          this.redirectService.redirect(response.authorization_url);
        }
      }
    } catch (error) {
      console.error('Error adding MCP server:', error);
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'WorkflowConnector::AddMcpServerModal': typeof AddMcpServerModal;
    'workflow-connector/add-mcp-server-modal': typeof AddMcpServerModal;
  }
}
